import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MessageTemplateAutocomplete from 'components/common/MessageTemplateAutocomplete';
import MessageTemplateSelector from 'components/common/MessageTemplateSelector';
import Editor from 'components/editor/Editor';
import Loading from 'components/loading/Loading';
import useApi from 'helpers/api';
import { useAuthenticatedUser } from 'hooks/useAuthenticatedUser';
import { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import PauseReasonsService from 'services/PauseReasons.service';

const TicketPauseRegistrationModal = ({
  ticketId,
  data,
  showModal,
  handleClose,
  handleSubmit,
  isLoading
}) => {
  const api = useApi();
  const { authenticatedUser } = useAuthenticatedUser();
  const [validated, setValidated] = useState(false);
  const [pauseReasons, setPauseReasons] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filters] = useState({ includeInactives: false });

  const initialState = {
    description: '',
    typeService: 'INTERNAL',
    timeSpent: '',
    startTime: '',
    endTime: '',
    pauseSla: false,
    pauseReason: { id: '' },
    user: { id: authenticatedUser?.id || '' }
  };
  const [body, setBody] = useState(initialState);
  const editorRef = useRef(null);
  const form = useRef(null);

  useEffect(() => {
    getPauseReasons();
    getAgents();
  }, []);

  useEffect(() => {
    if (showModal) {
      setValidated(false);
      setBody({
        ...initialState,
        timeSpent: data || '',
        user: { id: authenticatedUser?.id || '' }
      });
    }
  }, [showModal, data, authenticatedUser]);

  const getPauseReasons = async () => {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await PauseReasonsService.list(queryParams);

    response.content && setPauseReasons(response.content);
  };

  const getAgents = async () => {
    try {
      const queryParams = new URLSearchParams({
        page: 0,
        size: 1000,
        inactive: false
      }).toString();
      const response = await api.agents.list(queryParams);
      if (response.content) {
        setAgents(response.content);
      }
    } catch (error) {
      console.error('Erro ao buscar agentes:', error);
    }
  };

  const calculateTimeSpent = (start, end) => {
    if (!start || !end) return '';

    const [startHours, startMinutes] = start.split(':').map(Number);
    const [endHours, endMinutes] = end.split(':').map(Number);

    if (
      isNaN(startHours) ||
      isNaN(startMinutes) ||
      isNaN(endHours) ||
      isNaN(endMinutes)
    ) {
      return '';
    }

    let totalMinutes =
      endHours * 60 + endMinutes - (startHours * 60 + startMinutes);

    if (totalMinutes < 0) {
      totalMinutes += 24 * 60; // Handle next day
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
      2,
      '0'
    )}`;
  };

  const getTimeSpentStyle = () => {
    if (!(body.startTime && body.endTime)) return {};

    const isDark =
      document.documentElement.getAttribute('data-bs-theme') === 'dark';

    if (isDark) {
      return {
        backgroundColor: 'rgba(0, 210, 122, 0.2)',
        borderColor: 'rgba(0, 210, 122, 0.4)',
        color: '#00d27a'
      };
    }

    return {
      backgroundColor: '#d4edda',
      borderColor: '#c3e6cb'
    };
  };

  const onSubmit = e => {
    if (form.current.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
    } else {
      handleSubmit({
        description: body.description,
        typeService: body.typeService,
        timeSpent: body.timeSpent,
        startTime: body.startTime,
        endTime: body.endTime,
        pauseSla: body.pauseSla,
        pauseReason: body.pauseReason,
        user: body.user
      });
      clearFields();
    }
  };

  const clearFields = () => {
    setBody(initialState);
    setValidated(false);
  };

  const setFieldBody = (fieldName, value) => {
    setBody(prevBody => {
      const newBody = { ...prevBody };

      // Tratar campo user separadamente - sempre manter como objeto { id: number }
      if (fieldName === 'user') {
        newBody.user = { id: value };
      } else {
        newBody[fieldName] = value;
      }

      // Se startTime ou endTime foram alterados, calcular timeSpent quando ambos estiverem preenchidos
      if (fieldName === 'startTime' || fieldName === 'endTime') {
        const start = fieldName === 'startTime' ? value : newBody.startTime;
        const end = fieldName === 'endTime' ? value : newBody.endTime;

        if (start && end) {
          const calculatedTime = calculateTimeSpent(start, end);
          if (calculatedTime) {
            newBody.timeSpent = calculatedTime;
          }
        }
      }

      return newBody;
    });
  };

  return (
    <Modal size="lg" show={showModal} onHide={handleClose}>
      {isLoading && <Loading />}
      <Modal.Header closeButton>
        <Modal.Title>Pausar ticket</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form validated={validated} ref={form}>
          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="appointment.type">
              <Form.Label>
                Tipo <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                aria-label="Tipo"
                value={body.typeService}
                onChange={e => setFieldBody('typeService', e.target.value)}
                required
              >
                <option value="INTERNAL" key="INTERNAL">
                  Interno
                </option>
                <option value="EXTERNAL" key="EXTERNAL">
                  Externo
                </option>
              </Form.Select>
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Escolha uma opção!
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="6" controlId="appointment.agent">
              <Form.Label>
                Agente <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                aria-label="Agente"
                value={body.user?.id || ''}
                onChange={e =>
                  setFieldBody(
                    'user',
                    e.target.value ? Number(e.target.value) : ''
                  )
                }
                required
              >
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.firstName} {agent.lastName}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Selecione um agente!
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="4" controlId="appointment.startTime">
              <Form.Label>Hora Início</Form.Label>
              <Form.Control
                type="time"
                value={body.startTime}
                onChange={e => setFieldBody('startTime', e.target.value)}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="4" controlId="appointment.endTime">
              <Form.Label>Hora Fim</Form.Label>
              <Form.Control
                type="time"
                value={body.endTime}
                onChange={e => setFieldBody('endTime', e.target.value)}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="4" controlId="appointment.timeSpent">
              <Form.Label>
                Tempo <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="HH:MM"
                value={body.timeSpent}
                onChange={e => setFieldBody('timeSpent', e.target.value)}
                required
                readOnly={!!(body.startTime && body.endTime)}
                style={getTimeSpentStyle()}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Insira o tempo trabalhado!
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md={2}>
              <Form.Label>Pausar SLA</Form.Label>
              <Form.Switch
                value={body.pauseSla}
                onChange={() => setFieldBody('pauseSla', !body.pauseSla)}
                checked={body.pauseSla}
              />
            </Form.Group>

            {body.pauseSla && (
              <Form.Group as={Col} md={4}>
                <Form.Label>
                  Motivo de pausa <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  required={body.pauseReason}
                  value={body.pauseReason.id}
                  onChange={e =>
                    setBody({ ...body, pauseReason: { id: e.target.value } })
                  }
                >
                  <option value="">Selecione</option>
                  {pauseReasons.map((item, i) => (
                    <option value={item.id} key={i}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  Motivo de pausa obrigatório.
                </Form.Control.Feedback>
              </Form.Group>
            )}
          </Row>

          <Form.Group controlId="appointment.profile">
            <Form.Label className="d-flex align-items-center justify-content-between">
              <span>
                Descrição
                <span className="text-danger"> *</span>
              </span>
              <MessageTemplateSelector
                type="APPOINTMENT"
                ticketId={ticketId}
                editorRef={editorRef}
                onTemplateApplied={content => {
                  setFieldBody('description', content);
                }}
              />
            </Form.Label>
            <Editor
              data={body.description}
              value={value => setFieldBody('description', value)}
              ref={editorRef}
            />
            <MessageTemplateAutocomplete
              editorRef={editorRef}
              type="APPOINTMENT"
              ticketId={ticketId}
              onTemplateApplied={content => {
                setFieldBody('description', content);
              }}
            />
            <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              Insira uma descrição!
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="default" onClick={handleClose}>
          Fechar
        </Button>
        <Button variant="primary" type="submit" onClick={onSubmit}>
          <FontAwesomeIcon icon={faSave} className="me-2" />
          Salvar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TicketPauseRegistrationModal;
